import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

/**
 * Простая лента сообщений преподаватель↔ученик.
 * mode: 'teacher' | 'student'
 */
export default function StudentMessageThread({
  mode,
  studentId,
  teacherId,
  studentName,
  pollMs = 8000,
}) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const listRef = useRef(null);

  const endpoint = mode === 'teacher'
    ? `/messaging/teacher/students/${studentId}/messages/`
    : `/messaging/me/threads/${teacherId}/`;

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const params = {};
      if (mode === 'student' && studentId) params.student_id = studentId;
      const res = await api.get(endpoint, { params });
      const items = res.data?.messages || [];
      setMessages(items);
    } catch (err) {
      if (!silent) {
        setError(err.response?.data?.detail || 'Не удалось загрузить сообщения');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [endpoint, mode, studentId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!pollMs) return undefined;
    const id = setInterval(() => {
      load({ silent: true });
    }, pollMs);
    return () => clearInterval(id);
  }, [load, pollMs]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  async function sendMessage() {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    setError('');
    try {
      const payload = { body: text };
      if (mode === 'student' && studentId) payload.student_id = studentId;
      const res = await api.post(endpoint, payload);
      setMessages((prev) => [...prev, res.data]);
      setBody('');
    } catch (err) {
      setError(err.response?.data?.body || err.response?.data?.detail || 'Не отправилось');
    } finally {
      setSending(false);
    }
  }

  async function deleteMessage(messageId) {
    if (deletingId) return;
    if (!window.confirm('Удалить это сообщение? Собеседник увидит пометку «Сообщение удалено».')) {
      return;
    }
    setDeletingId(messageId);
    setError('');
    try {
      const res = await api.delete(`/messaging/messages/${messageId}/`);
      setMessages((prev) => prev.map((m) => (m.id === messageId ? res.data : m)));
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось удалить сообщение');
    } finally {
      setDeletingId(null);
    }
  }

  function handleSend(e) {
    e.preventDefault();
    void sendMessage();
  }

  function handleComposeKeyDown(e) {
    if ((e.key === 'Enter' || e.code === 'Enter') && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void sendMessage();
    }
  }

  const myUserId = user?.id;

  return (
    <section className="ops-thread">
      <h2 className="ops-thread-title">
        {mode === 'teacher'
          ? `Сообщения${studentName ? `: ${studentName}` : ''}`
          : 'Переписка с преподавателем'}
      </h2>
      <p className="muted ops-thread-hint">
        Организационные вопросы и короткие ответы по учёбе. Это не мессенджер:
        ссылки на урок и переносы лучше через кнопки в расписании.
      </p>
      {error && <div className="alert alert-error">{error}</div>}
      {loading ? (
        <p className="muted">Загрузка…</p>
      ) : (
        <div className="ops-thread-list" ref={listRef}>
          {messages.length === 0 ? (
            <p className="muted">Пока пусто — напишите первое сообщение.</p>
          ) : (
            messages.map((m) => {
              const canDelete = Boolean(myUserId) && m.author_id === myUserId && !m.deleted;
              return (
                <div
                  key={m.id}
                  className={`ops-bubble ${m.is_mine ? 'ops-bubble--mine' : 'ops-bubble--theirs'}${
                    m.author_role === 'parent' ? ' ops-bubble--parent' : ''
                  }${m.deleted ? ' ops-bubble--deleted' : ''}`}
                >
                  <div className="ops-bubble-meta">
                    <span>
                      {m.author_name}
                      {' · '}
                      {new Date(m.created_at).toLocaleString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                    {canDelete && (
                      <button
                        type="button"
                        className="ops-bubble-delete"
                        disabled={deletingId === m.id}
                        onClick={() => void deleteMessage(m.id)}
                      >
                        {deletingId === m.id ? '…' : 'Удалить'}
                      </button>
                    )}
                  </div>
                  <div className="ops-bubble-body">
                    {m.deleted ? 'Сообщение удалено' : m.body}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
      <form className="ops-thread-compose" onSubmit={handleSend}>
        <textarea
          className="form-input"
          rows={2}
          placeholder="Текст сообщения… Ctrl+Enter — отправить"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={handleComposeKeyDown}
          maxLength={4000}
        />
        <button type="submit" className="btn btn-primary" disabled={sending || !body.trim()}>
          {sending ? '…' : 'Отправить'}
        </button>
      </form>
    </section>
  );
}
